"use client";

import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Area, AreaChart
} from "recharts";
import { formatCurrency } from "@/lib/utils";
import type { PriceHistoryPoint } from "@/types";

interface PriceHistoryChartProps {
  lowestPrice: number;
  history?: PriceHistoryPoint[];
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
        <p className="text-muted-foreground mb-1">{label}</p>
        <p className="font-bold text-primary text-base">{formatCurrency(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

export function PriceHistoryChart({ lowestPrice, history = [] }: PriceHistoryChartProps) {
  const data = history
    .map((point) => {
      const date = new Date(point.date);
      const time = date.getTime();
      return {
        date: Number.isFinite(time)
          ? date.toLocaleDateString("es-MX", { month: "short", day: "numeric" })
          : point.date,
        price: point.price,
        time: Number.isFinite(time) ? time : 0,
      };
    })
    .sort((a, b) => a.time - b.time);
  if (data.length === 0) {
    return (
      <div className="min-h-[220px] rounded-xl border border-dashed bg-muted/20 flex items-center justify-center text-sm text-muted-foreground text-center px-4">
        Todavía no hay historial de precio almacenado.
      </div>
    );
  }
  const avgPrice = data.reduce((sum, d) => sum + d.price, 0) / data.length;

  // Only show every 15th label
  const tickFormatter = (_: string, index: number) => {
    if (index % 15 === 0) return data[index]?.date ?? "";
    return "";
  };

  return (
    <div className="w-full h-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
          <defs>
            <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, fill: "#94a3b8" }}
            tickLine={false}
            axisLine={false}
            tickFormatter={tickFormatter}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#94a3b8" }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`}
            width={45}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine
            y={avgPrice}
            stroke="#f59e0b"
            strokeDasharray="4 4"
            label={{ value: "Promedio", position: "right", fontSize: 10, fill: "#f59e0b" }}
          />
          <ReferenceLine
            y={lowestPrice}
            stroke="#22c55e"
            strokeDasharray="4 4"
            label={{ value: "Mínimo", position: "right", fontSize: 10, fill: "#22c55e" }}
          />
          <Area
            type="monotone"
            dataKey="price"
            stroke="#2563eb"
            strokeWidth={2.5}
            fill="url(#priceGradient)"
            dot={false}
            activeDot={{ r: 5, fill: "#2563eb", stroke: "#fff", strokeWidth: 2 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
